// PurchaseOrderForm — /procurement/new. Build ONE draft purchase order with as
// many lines as you can type, then hand it to the PO detail page for review and
// submit.
//
// ── ONE SITTING IS ONE ORDER ─────────────────────────────────────────────────
// Everything staged here goes out as a single POST to
// /procurement/purchase-orders, and the approval threshold is measured against
// that order's real subtotal at submit time, on the detail page. A form that
// created one PO per row is how a $6,000 buy typed as four rows slipped under a
// $2,500 threshold four times over, so the number of documents one press can
// produce is exactly one.
//
// ── RAPID ENTRY IS THE PRODUCT ───────────────────────────────────────────────
// Type a row, press Enter, type the next. Every focus move is deliberate: mount
// lands on the vendor, Enter in a header field moves to the entry row, staging
// a line returns to the entry row's first field, and the New Item wizard hands
// back to quantity because the item is settled and how many is the only thing
// left on that row. Ctrl/Cmd+Enter anywhere on the page creates the draft, so
// the whole document can be typed without reaching for a mouse.
//
// ── THE ROW ITSELF IS PoLineEntry.tsx ────────────────────────────────────────
// The three kinds of line, the fields each kind has, the picker that is the only
// way to reference an item, and the New Item wizard behind it all live in that
// component, because the PO detail page adds lines to an order that already
// exists with the same row. Read its header for any of that.
//
// What stays here is the DOCUMENT: one vendor, the list of staged lines, the
// running total, and the one press that creates the draft. The row hands over a
// validated StagedLine and this page decides where it goes.
//
// ── THE VENDOR FIELD IS SHARED ───────────────────────────────────────────────
// components/VendorPicker.tsx owns the fetch, the option shape and the hold
// predicate. This page keeps only the wrapper div around it, because
// EntityCombobox takes no ref: the wrapper is what focus and the
// Enter-to-advance handler can actually reach.
//
// ── WHAT onSubmitted MEANS ───────────────────────────────────────────────────
// "I am done here", NOT "a PO was created". It fires once, from the header's
// Done action or on unmount if the user leaves without pressing it, never per
// create. Firing it after every create would destroy the vendor-A-then-vendor-B
// loop this form is built for. Routed usage (the normal case) passes neither
// prop, and Done and Cancel then both mean "back to the PO list".
//
// `git log -- web/src/Procurement/PurchaseOrderForm.tsx` has how it got here.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import {
  usePoApi,
  formatCents,
  PO_LINE_TYPE_COLORS,
  PO_LINE_TYPE_LABELS,
  REORDER_PREFILL_SESSION_KEY,
  type PoLineInput,
  type ReorderDraftPrefill,
} from './poApi';
import { type PartsCatalogRow } from '../Receiving/types';
import PageHeader, { SecondaryAction } from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { Alert } from '../components/ui/Alert';
import Appear from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';
import { EmptyState } from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import PaymentClockFields from './PaymentClockFields';
import { describeClock, isPaymentTermsBasis, type PaymentTermsBasis } from './paymentClock';
import { modalInput, modalLabel } from '../components/review/fieldStyles';
import { buttonClass, IconButton } from '../components/ui/Button';
import { Tooltip } from '../components/ui/Tooltip';
import { Checkbox } from '../components/ui/Checkbox';
import { Field } from '../components/ui/Field';
import { IconClose } from '../components/ui/icons';
// ONE vendor picker, everywhere a supplier is asked for. It fetches
// /procurement/vendors itself and hands back the whole row.
import VendorPicker, { onHoldNow, type VendorPickerVendor } from '../components/VendorPicker';
import ItemName, { itemLabel } from '../components/ItemName';
// The entry row, shared with the PO detail page. It owns everything about
// typing a line; this page owns what happens to the line afterwards.
import PoLineEntry, {
  type PoLineEntryHandle,
  type StagedDiscount,
  type StagedLine,
} from './PoLineEntry';
// Moving a staged line: the grip cell and the drag context, shared with the
// detail page's edit mode. Charges sink to the bottom on both.
import { CARRIED_ROW, LineGrip, LineOrder, insertPinned, movePinned } from './LineOrder';

const isChargeStaged = (l: StagedLine) => l.lineType === 'charge';

// The reorder panel's handoff key and payload both live in poApi.ts, which owns
// every PO API-shape assumption. Read once on mount and cleared, so a later
// visit that has nothing to do with that suggestion doesn't inherit it.
//
// The panel lives on PurchaseOrderList (Procurement/ReorderPanel.tsx) and its
// "Draft a PO" action writes this key, then navigates straight to
// /procurement/new — this page — so the prefill lands on the very next mount.

interface CreatedPo {
  id: string;
  po_number: string;
}

/** What we actually accept off sessionStorage. Every field is optional and
 * quantity may arrive as a string: this is parsed JSON from another surface,
 * not a value the type system got to check. */
type PrefillPayload = Partial<Omit<ReorderDraftPrefill, 'quantity' | 'vendor_name'>> & {
  quantity?: number | string;
  vendor_name?: string | null;
};

interface Props {
  // "The user is finished." Fired from Done, not from each create — see the
  // file header. Only supplied by tests today; the routed page (the normal
  // case) leaves both props unset and navigates to /procurement itself.
  onSubmitted?: () => void;
  onCancel?: () => void;
}

function apiErrorText(err: any, fallback: string): string {
  return err?.data?.error?.message || err?.message || fallback;
}

// Alignment is appended per column rather than overridden: two text-align
// utilities in one class list are resolved by their order in the STYLESHEET,
// not in the attribute, so "the later one wins" is luck.
const TH_BASE = 'font-normal text-[11px] uppercase tracking-wider text-on-surface-variant py-1.5 pr-2';
const TH = `${TH_BASE} text-left`;
const TH_NUM = `${TH_BASE} text-right`;
const TD = 'py-1.5 pr-2';

const PurchaseOrderForm: React.FC<Props> = ({ onSubmitted, onCancel }) => {
  const api = useApi();
  const poApi = usePoApi();
  const toast = useToast();
  const history = useHistory();
  // Routed usage (the normal case, /procurement/new) passes neither prop —
  // "done" and "cancel" both mean "back to the PO list" then. A host that DOES
  // pass onSubmitted/onCancel (only the tests today) keeps full control.
  const goToList = useCallback(() => history.push('/procurement'), [history]);

  // --- Header (one vendor per PO, because that is what a PO is) ---
  // The whole vendor row, not just its id: the hold warning under the field
  // reads release_date off it, and the picker hands it over on select.
  const [vendor, setVendor] = useState<VendorPickerVendor | null>(null);
  const vendorId = vendor?.id ?? '';
  const [quoteRef, setQuoteRef] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [headerNotes, setHeaderNotes] = useState('');
  // Split from headerNotes by AUDIENCE, not by topic: this one prints on the
  // PDF the vendor receives, headerNotes never does. See poApi.ts.
  const [vendorNotes, setVendorNotes] = useState('');
  // --- Payment clock (migration 078) ---
  // Prefilled from the vendor's default the moment one is picked, then this
  // order's own: whatever the two fields hold is what the PO snapshots.
  const [clockDays, setClockDays] = useState('');
  const [clockBasis, setClockBasis] = useState('');
  /** The vendor's CURRENT default, off the gated detail row. Null until it
   *  loads (or when the fetch failed, in which case the fields start blank). */
  const [vendorClock, setVendorClock] = useState<{ days: number | null; basis: PaymentTermsBasis | null } | null>(null);
  // "Update as you go": ON when the vendor has no clock yet, OFF when it has.
  const [saveClockToVendor, setSaveClockToVendor] = useState(false);

  // --- Staged lines ---
  const [lines, setLines] = useState<StagedLine[]>([]);

  // --- The reorder handoff, on its way to the entry row ---
  /** What the row should start with. The row applies a NEW object, so the
   *  upgrade below re-hands it rather than editing this one in place. */
  const [entryPrefill, setEntryPrefill] = useState<{
    item: PartsCatalogRow;
    quantity?: string;
  } | null>(null);
  // The SKU a reorder handoff arrived with, held only long enough to fetch the
  // real catalog row behind it. Null when there was no handoff.
  const [prefilledSku, setPrefilledSku] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedPo[]>([]);

  const vendorRef = useRef<HTMLDivElement>(null);
  // The entry row, reached for the two things this page still decides: where
  // the cursor goes, and when the row is emptied. Everything inside it is its
  // own business.
  const entryRef = useRef<PoLineEntryHandle>(null);
  // Vendor name from the prefill, held until the vendor list arrives.
  const pendingVendorName = useRef<string | null>(null);
  // Latest "did we create anything / has the hub been told" pair, read from an
  // unmount cleanup where state would be stale.
  const createdCountRef = useRef(0);
  const toldTheHubRef = useRef(false);
  const onSubmittedRef = useRef(onSubmitted);
  onSubmittedRef.current = onSubmitted;

  // Where the next line starts, after Enter in the header and whenever this page
  // sends somebody back to typing. Rapid entry depends on it: without it you add
  // a line and then reach for the mouse. Which field that is depends on the kind
  // of line, which is why the row answers this rather than the page.
  const focusEntry = useCallback(() => entryRef.current?.focus(), []);

  // The vendor picker gets held by its wrapper because EntityCombobox takes no
  // ref, so we focus the field inside it.
  const focusVendor = useCallback(() => {
    vendorRef.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, []);

  /** Empty the entry row. Used when this document is finished, so a half-typed
   *  line does not follow the buyer onto the next vendor's order. The vendor,
   *  the dates and the staged lines are not the row's and survive it. */
  const clearEntryRow = useCallback(() => entryRef.current?.clear(), []);

  // Tells a supplied host once, guarded against firing twice (Done, then
  // unmount). Does NOT navigate — that is finish()'s job, and forcing a
  // navigation from an unmount cleanup would fight whatever navigation the
  // user is already mid-flight on (e.g. leaving via the top nav).
  const notifyHost = useCallback(() => {
    if (toldTheHubRef.current) return;
    toldTheHubRef.current = true;
    onSubmittedRef.current?.();
  }, []);

  // Done action: tell a host if one was supplied; otherwise this page's job is
  // finished — go back to the PO list.
  const finish = useCallback(() => {
    notifyHost();
    if (!onSubmittedRef.current) goToList();
  }, [notifyHost, goToList]);

  // Leaving via the browser back button, the top nav, or any other route change
  // skips Done, and a host (drawer-style usage) would otherwise never hear that
  // lines were created. Tell it on the way out — never navigate on its behalf.
  useEffect(
    () => () => {
      if (createdCountRef.current > 0) notifyHost();
    },
    [notifyHost]
  );

  // Enter in a header field means "on to the rows", not "add an empty line".
  // A modified Enter is the create shortcut and belongs to the page, so it
  // passes through untouched.
  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    focusEntry();
  };

  // The vendor picker's wrapper sees the same key AFTER the combobox has had it.
  // defaultPrevented is how we tell "it took the highlighted vendor" from
  // "nothing was open, so this Enter is the header's".
  const handleVendorKeyDown = (e: React.KeyboardEvent) => {
    if (e.defaultPrevented) return;
    handleHeaderKeyDown(e);
  };

  // Prefill from the reorder panel. Contract is the sessionStorage key alone:
  // { sku, part_catalog_id, quantity, vendor_name }. Anything missing or
  // malformed is skipped rather than blocking the form.
  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(REORDER_PREFILL_SESSION_KEY);
      if (raw) sessionStorage.removeItem(REORDER_PREFILL_SESSION_KEY);
    } catch {
      return; // storage disabled; nothing to prefill
    }
    if (!raw) return;

    let payload: PrefillPayload;
    try {
      payload = JSON.parse(raw) as PrefillPayload;
    } catch {
      return;
    }
    if (!payload || typeof payload !== 'object') return;

    // The reorder panel always sends both halves. An id without a sku, or a sku
    // without an id, cannot be staged: the endpoint takes catalog ids and the
    // picker shows what was chosen, so half a reference is no reference.
    if (payload.sku && Number.isInteger(payload.part_catalog_id) && (payload.part_catalog_id as number) > 0) {
      const qty = parseInt(String(payload.quantity ?? ''), 10);
      // The handoff carries an id and a SKU and nothing else (see
      // ReorderDraftPrefill in poApi.ts). That is enough for the picker to show
      // the item and for Add line to stage it; the cast says the rest of the
      // catalog row is not read, rather than pretending it arrived.
      //
      // Item and quantity go over as ONE object because the row applies a
      // prefill whole: a count with no item to count is not a prefill.
      setEntryPrefill({
        item: {
          id: payload.part_catalog_id as number,
          sku: payload.sku,
        } as PartsCatalogRow,
        ...(Number.isFinite(qty) && qty > 0 ? { quantity: String(qty) } : {}),
      });
      setPrefilledSku(payload.sku);
    }
    if (payload.vendor_name) pendingVendorName.current = payload.vendor_name;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve the prefill's vendor name the moment the picker has its list, which
  // is the only reason VendorPicker reports that at all. Name match is
  // case-insensitive exact, the same rule vendors_name_uniq enforces.
  const handleVendorsLoaded = useCallback((vendors: VendorPickerVendor[]) => {
    const wanted = pendingVendorName.current;
    if (!wanted) return;
    pendingVendorName.current = null;
    const match = vendors.find((v) => v.name.toLowerCase() === wanted.trim().toLowerCase());
    if (match) setVendor(match);
    else toast.warning?.(`No vendor named "${wanted}" yet. Pick one to continue.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus the vendor picker on mount. Parent effects run after child effects
  // and a picker's own list can grab attention, so a plain autoFocus loses that
  // race. The delay is what makes this deterministic, not decoration.
  useEffect(() => {
    const t = setTimeout(() => focusVendor(), 60);
    return () => clearTimeout(t);
  }, [focusVendor]);

  // The picker's list row omits the clock (commercial detail, off the list
  // route), so the default comes off the gated detail row once a vendor is
  // chosen. A stale response for a vendor no longer selected is dropped.
  useEffect(() => {
    // Reset FIRST, on every change: a vendor switch must not leave the previous
    // vendor's clock, or a ticked box, in place while the new row is in flight,
    // or a fast Create writes one vendor's numbers as another's default.
    setClockDays('');
    setClockBasis('');
    setVendorClock(null);
    setSaveClockToVendor(false);
    if (!vendorId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const full = (await api.get(`/procurement/vendors/${vendorId}`)) as {
          payment_terms_days: number | null; payment_terms_basis: string | null;
        };
        if (cancelled) return;
        const days = full.payment_terms_days ?? null;
        const basis = isPaymentTermsBasis(full.payment_terms_basis) ? full.payment_terms_basis : null;
        setVendorClock({ days, basis });
        setClockDays(days === null ? '' : String(days));
        setClockBasis(basis || '');
        setSaveClockToVendor(days === null && basis === null);
      } catch {
        if (cancelled) return;
        // Said out loud: with the default unknown, the create sends no clock
        // keys and the server snapshots the vendor's default itself.
        toast.warning?.('Could not load this vendor\'s payment terms. The order will take the vendor\'s default.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  // An effective hold blocks SUBMIT, not create. Say so up front rather than
  // letting the buyer find out on the detail page.
  const vendorHoldWarning = useMemo(() => {
    if (!vendor || !onHoldNow(vendor)) return null;
    return vendor.release_date
      ? `${vendor.name} is on hold until ${vendor.release_date}. You can draft this PO, but it cannot be submitted before then.`
      : `${vendor.name} is on hold. You can draft this PO, but it cannot be submitted until the hold is lifted.`;
  }, [vendor]);

  // Upgrade the reorder prefill to the real catalog row, so the picker shows the
  // item's NAME rather than the SKU the handoff carried. Best effort on purpose:
  // if it fails or finds nothing, the synthesized row above is still a valid
  // reference and the line stages exactly as it did before.
  //
  // Handed over as a NEW prefill rather than written into the row: the row
  // refuses one whose item is not the one it is holding, so an upgrade that
  // lands after the buyer has picked something else cannot overwrite it.
  useEffect(() => {
    if (!prefilledSku) return;
    let cancelled = false;
    (async () => {
      try {
        const data = (await api.get(
          `/parts-catalog?q=${encodeURIComponent(prefilledSku)}&page_size=20`
        )) as { rows: PartsCatalogRow[] };
        const match = (data.rows || []).find((r) => r.sku === prefilledSku);
        if (match && !cancelled) setEntryPrefill({ item: match });
      } catch {
        // Silent: the prefill already works without this.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledSku]);

  // Unit times quantity, whatever the line's kind: a service or charge line
  // carries quantity 1, so it contributes its amount once and the arithmetic
  // needs no special case.
  const subtotalCents = useMemo(
    () =>
      lines.reduce(
        (sum, l) => sum + (l.unitCostCents === null ? 0 : l.unitCostCents * l.quantity),
        0
      ),
    [lines]
  );
  const someLineHasNoCost = useMemo(() => lines.some((l) => l.unitCostCents === null), [lines]);

  // The order's ONE discount (migration 110). Staged from the same entry row as
  // a line and drawn beneath the lines, but it is not in `lines`: it goes to the
  // server as header fields. A percent is shown against the subtotal as it
  // stands, which is also what the server will compute.
  const [discount, setDiscount] = useState<StagedDiscount | null>(null);
  const discountCents = useMemo(() => {
    if (!discount) return 0;
    return discount.pct !== null ? Math.round((subtotalCents * discount.pct) / 100) : discount.cents;
  }, [discount, subtotalCents]);
  const totalCents = subtotalCents - discountCents;
  // Judged only once there are lines: a staged discount on an empty order is
  // waiting, not wrong.
  const discountTooBig = lines.length > 0 && discountCents > subtotalCents;

  // Asked by the row before it validates anything. One vendor per PO is this
  // page's rule and not the row's, so the refusal and the focus move are this
  // page's too.
  const canStageLine = () => {
    if (!vendorId) {
      toast.error('Pick the vendor first. Every line on a PO comes from one vendor.');
      focusVendor();
      return false;
    }
    return true;
  };

  // A validated line, off the row. It has already cleared and refocused itself;
  // all this page does is put the line on the document. Creating the PO is a
  // separate, deliberate press — one per vendor, not one per row.
  // A new line lands where the document will print it: a charge under
  // everything, anything else above the first charge. The order staged here IS
  // the order sent, and the server numbers the lines by it.
  const handleStageLine = (line: StagedLine) => setLines((prev) => insertPinned(prev, line, isChargeStaged));
  const handleStageDiscount = (next: StagedDiscount) => {
    if (discount) toast.info('Replaced the discount.');
    setDiscount(next);
  };

  const removeLine = (key: string) => setLines((prev) => prev.filter((l) => l.key !== key));

  const moveLine = (from: number, to: number) => setLines((prev) => movePinned(prev, from, to, isChargeStaged));
  const stepLine = (key: string, delta: -1 | 1) => {
    const from = lines.findIndex((l) => l.key === key);
    if (from >= 0) moveLine(from, from + delta);
  };
  const [carriedKey, setCarriedKey] = useState<string | null>(null);
  const stagedLabel = (l: StagedLine) => (l.lineType === 'stock' ? itemLabel(l) : l.lineDescription || 'line');

  const canCreate = !creating && lines.length > 0 && !!vendorId;

  const handleCreate = async () => {
    if (creating) return;
    if (!vendorId) {
      toast.error('Pick a vendor before creating the PO');
      focusVendor();
      return;
    }
    if (lines.length === 0) {
      toast.error('Add at least one line first');
      focusEntry();
      return;
    }
    if (discountTooBig) {
      toast.error('The discount is larger than the lines. Lower it or remove it.');
      return;
    }
    // A typed-but-unadded line or discount would vanish with the create and the
    // order would look right. Same guard the detail page's Save applies.
    if (entryRef.current?.hasContent()) {
      toast.error('Add what you typed in the entry row, or clear it, before creating.');
      focusEntry();
      return;
    }

    const ref = quoteRef.trim();
    const poLines: PoLineInput[] = lines.map((l) => {
      const shared = {
        // Header-level date, stamped per line: the receiving inbox's overdue
        // logic reads the LINE's date, not the header's.
        expected_arrival_date: expectedDate || null,
        // Same value in both places on purpose. The header keeps the vendor's
        // own number as quote_number; the line keeps it because receipt boxes
        // inherit purchase_order_ref from the line at receive time
        // (receipt-service.js).
        purchase_order_ref: ref || null,
      };
      if (l.lineType === 'stock') {
        return {
          ...shared,
          line_type: 'stock' as const,
          part_catalog_id: l.partCatalogId,
          revision: l.revision,
          quantity_expected: l.quantity,
          unit_cost_cents: l.unitCostCents,
          notes: l.notes,
        };
      }
      return {
        ...shared,
        line_type: l.lineType,
        // No part_catalog_id at all: a service or charge line has no item, and
        // the column is NULL for exactly this reason.
        //
        // `description` is a WRITE-TIME ALIAS for `notes` (validateLine in
        // purchase-orders-service.js): a PO line has one free-text column, and
        // on a line with no item that column is what the line IS. That is why
        // the entry row offers these two kinds no separate note field; a note
        // sent beside a description would be dropped by the server.
        description: l.lineDescription,
        quantity_expected: 1,
        unit_cost_cents: l.unitCostCents,
      };
    });

    if (clockDays.trim() !== '') {
      const n = Number(clockDays);
      if (!Number.isInteger(n) || n < 0 || n > 365) {
        toast.error('Net days must be a whole number, 0 to 365.');
        return;
      }
    }
    if ((clockDays.trim() === '') !== (clockBasis === '')) {
      toast.error(clockBasis === ''
        ? 'Say what the net days count from, or clear them.'
        : 'Net days are needed for that payment clock, or clear what it counts from.');
      return;
    }
    // Sent explicitly, blank included, once the vendor's default is known: the
    // fields were prefilled from it, so what they hold is this order's clock.
    // With the default unknown (fetch failed or still in flight) and nothing
    // typed, the keys are omitted and the server snapshots the default itself.
    const clockKnown = vendorClock !== null || clockDays.trim() !== '' || clockBasis !== '';

    setCreating(true);
    try {
      const po = await poApi.create({
        vendor_id: vendorId,
        lines: poLines,
        ...(ref ? { quote_number: ref } : {}),
        ...(expectedDate ? { expected_date: expectedDate } : {}),
        ...(headerNotes.trim() ? { notes: headerNotes.trim() } : {}),
        ...(vendorNotes.trim() ? { vendor_notes: vendorNotes.trim() } : {}),
        ...(discount ? {
          discount_pct: discount.pct,
          discount_cents: discount.pct === null ? discount.cents : 0,
          discount_reason: discount.reason,
        } : {}),
        ...(clockKnown ? {
          terms_days: clockDays.trim() === '' ? null : Number(clockDays),
          terms_basis: isPaymentTermsBasis(clockBasis) ? clockBasis : null,
        } : {}),
      });

      // "Update as you go": the order is created either way; the vendor
      // write is a courtesy that reports its own failure.
      let vendorLine = '';
      const clockSet = po.terms_days != null || po.terms_basis != null;
      const vendorDiffers = !vendorClock
        || vendorClock.days !== po.terms_days || vendorClock.basis !== po.terms_basis;
      if (saveClockToVendor && vendor && clockSet && vendorDiffers) {
        try {
          await api.patch(`/procurement/vendors/${vendor.id}`, {
            body: { payment_terms_days: po.terms_days, payment_terms_basis: po.terms_basis },
          });
          vendorLine = ` ${vendor.name} now defaults to this payment clock.`;
        } catch (err: any) {
          toast.error(`The vendor default did not save: ${err?.data?.error?.message || err?.message || 'request failed'}`);
        }
      }
      toast.success(`Draft ${po.po_number} created. Review and submit it.${vendorLine}`);
      createdCountRef.current += 1;
      setCreated((prev) => [{ id: po.id, po_number: po.po_number }, ...prev]);

      // Clear for the next vendor and put the cursor back where the loop starts.
      setLines([]);
      setDiscount(null);
      setVendor(null);
      setQuoteRef('');
      setExpectedDate('');
      setHeaderNotes('');
      setVendorNotes('');
      clearEntryRow();
      focusVendor();
    } catch (err: any) {
      toast.error(apiErrorText(err, 'Failed to create the purchase order'));
    } finally {
      setCreating(false);
    }
  };

  // Ctrl/Cmd+Enter is "I am done typing this order", from anywhere in it. Same
  // conditions as the button: when that pill is disabled this does nothing, so
  // the shortcut can never do something the visible control refuses to.
  const handleFormKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey)) return;
    // A picker with its list open takes Enter to commit the highlighted row and
    // says so with preventDefault. Creating the order off that same keystroke
    // would finish a document the person was still filling in.
    if (e.defaultPrevented) return;
    e.preventDefault();
    if (!canCreate) return;
    handleCreate();
  };

  return (
    <PageLayout kind="workspace" className="pt-4 sm:pt-5 space-y-5">
        <PageHeader
          title="New purchase order"
          helpTitle="About creating a purchase order"
          lede="One vendor, as many lines as you need. You get a draft; submitting happens on the order's own page."
          help={
            <>
              <p>
                This page builds ONE purchase order with as many lines as you need. Type a
                line, press Enter, type the next. Nothing is sent to a vendor from here: you
                end up with a draft, and submitting it happens on the order&apos;s own page
                once you have looked it over.
              </p>
              <p className="font-medium text-on-surface">Why one order and not one per line</p>
              <p>
                An earlier version made every row its own order, which meant a $6,000 buy
                typed as four rows slipped under a $2,500 approval threshold four times over.
                The threshold is now measured against the whole order, at submit.
              </p>
              <p className="font-medium text-on-surface">Naming a part</p>
              <p>
                Type any part of its name, SKU or part number and pick it from the list.
                Nothing there? The last row of the list offers to create it, and what you
                typed carries over as its name.
              </p>
              <p>
                That opens the same New Item wizard the parts catalog uses, so an item
                created here is a full item: named, classified, and findable by everyone
                tomorrow. It is created the moment you finish the wizard, not when you
                finish the order, and it lands straight back in the field that asked for it.
              </p>
              <p>
                The revision box beside the picker is an override. Leave it empty and the
                line carries whatever revision the catalog records for that item.
              </p>
              <p className="font-medium text-on-surface">Service and charge lines</p>
              <p>
                Not everything on an order is a part. A service line is work the vendor does
                for you (an X-ray inspection, tooling, a test run); a charge line is money on
                the order that is neither a part nor work (freight, duty, a setup fee). Both
                take a description and one amount, with no quantity and no item, and neither
                ever reaches receiving: there is nothing to put on a shelf.
              </p>
              <p className="font-medium text-on-surface">Shipping somewhere else</p>
              <p>
                Orders default to your own address. If the vendor should ship straight to
                somebody else, usually a contract assembler doing the next step, set the
                ship-to on the order. Those goods are still yours and still get received,
                just to that vendor&apos;s premises rather than your shelf.
              </p>
            </>
          }
          actions={
            <>
              {/* The quote lane's door, on the page people open when they mean
                  to place an order. Its other door is the list; somebody who
                  arrived here from "New Purchase Order" never saw that one.
                  Tonal, per PageHeader's rule: another way to START work, not a
                  place to go and look. Only before the first line lands, and
                  never over a reorder prefill: that prefill is consumed on
                  mount, so leaving this page throws it away. */}
              {created.length === 0 && !prefilledSku && (
                <SecondaryAction to="/procurement/quote">
                  Start from a quote
                </SecondaryAction>
              )}
              {created.length > 0 ? (
                <SecondaryAction variant="text" onClick={finish}>
                  Done
                </SecondaryAction>
              ) : (
                <SecondaryAction variant="text" onClick={onCancel ?? goToList}>
                  Cancel
                </SecondaryAction>
              )}
            </>
          }
        />

        {/* A div, not a <form>: the entry row is the form on this page, which
            is what makes Enter stage a line there. Ctrl/Cmd+Enter is the
            DOCUMENT's shortcut, so it is caught up here where every field is
            inside it, and the row lets it pass. */}
        <div onKeyDown={handleFormKeyDown}>
          <div className="space-y-4">
            {/* ---------------- Header: one vendor, one document ---------------- */}
            <Panel label="Vendor and terms" className="space-y-3">
              <div className="grid gap-3 md:grid-cols-3">
                {/* Enter in a header field means "on to the rows" — but the
                    combobox owns Enter while its list is open, where it takes
                    the highlighted vendor and calls preventDefault. So only an
                    Enter it did not use advances, and the second press (list now
                    shut) still does. */}
                <div ref={vendorRef} onKeyDown={handleVendorKeyDown}>
                  <VendorPicker
                    id="po-vendor"
                    label="Vendor (required)"
                    // This field is focused at mount for rapid entry, and a list
                    // that opens on that focus lands on top of Expected arrival
                    // before anybody has asked for it. Typing opens it, and so
                    // does the down arrow, so the fast path is untouched and a
                    // first click at the next field goes where it was aimed.
                    openOnFocus={false}
                    value={vendorId || null}
                    onSelect={setVendor}
                    onVendorsLoaded={handleVendorsLoaded}
                    placeholder="Type to search"
                    labelClassName={modalLabel}
                    inputClassName={modalInput}
                  />
                </div>
                <div>
                  <label className={modalLabel} htmlFor="po-quote">Vendor quote / ref #</label>
                  <Field
                    id="po-quote"
                    thumb
                    value={quoteRef}
                    onChange={(e) => setQuoteRef(e.target.value)}
                    onKeyDown={handleHeaderKeyDown}
                    placeholder="Q-2026-0412"
                  />
                </div>
                <div>
                  <label className={modalLabel} htmlFor="po-expected">Expected arrival</label>
                  <Field
                    id="po-expected"
                    thumb
                    type="date"
                    value={expectedDate}
                    onChange={(e) => setExpectedDate(e.target.value)}
                    onKeyDown={handleHeaderKeyDown}
                  />
                </div>
              </div>

              <Reveal show={!!vendor}>
                {vendor && (
                  <div className="-m-0.5 grid gap-3 p-0.5 md:grid-cols-3 md:items-end">
                    <PaymentClockFields
                      idPrefix="po-clock"
                      days={clockDays}
                      basis={clockBasis}
                      onDaysChange={setClockDays}
                      onBasisChange={setClockBasis}
                      labelClassName={modalLabel}
                    />
                    <Checkbox
                      className="pb-2"
                      checked={saveClockToVendor}
                      onChange={(e) => setSaveClockToVendor(e.target.checked)}
                      label={`Save as ${vendor.name}'s default`}
                      description={vendorClock && describeClock(vendorClock.days, vendorClock.basis)
                        ? `Currently ${describeClock(vendorClock.days, vendorClock.basis)}`
                        : 'No payment clock recorded for this vendor yet'}
                    />
                  </div>
                )}
              </Reveal>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className={modalLabel} htmlFor="po-notes">Internal notes</label>
                  <Field
                    id="po-notes"
                    thumb
                    value={headerNotes}
                    onChange={(e) => setHeaderNotes(e.target.value)}
                    onKeyDown={handleHeaderKeyDown}
                    placeholder="Context for the team, never printed"
                  />
                </div>
                {/* The hint underneath is what stops a vendor instruction
                    landing in the internal field beside it. */}
                <div>
                  <label className={modalLabel} htmlFor="po-vendor-notes">Notes to vendor</label>
                  <Field
                    id="po-vendor-notes"
                    thumb
                    value={vendorNotes}
                    onChange={(e) => setVendorNotes(e.target.value)}
                    onKeyDown={handleHeaderKeyDown}
                    placeholder="Shipping instructions, packaging, anything the vendor should read"
                  />
                  <p className="mt-1 text-xs text-on-surface-variant">Prints on the PDF, under the totals.</p>
                </div>
              </div>

              {/* A colored hairline on a tinted callout: the one place this
                  house style still draws a border, because the color IS the
                  message. */}
              <Reveal show={!!vendorHoldWarning}>
                <Alert tone="warning" variant="note" className="!flex">
                  {vendorHoldWarning}
                </Alert>
              </Reveal>
            </Panel>

            {/* ---------------- Lines: the row, then what it staged ---------------- */}
            <Panel label="Lines" className="space-y-4">
              <PoLineEntry
                ref={entryRef}
                onStage={handleStageLine}
                onStageDiscount={handleStageDiscount}
                canStage={canStageLine}
                disabled={creating}
                prefill={entryPrefill}
              />

              {/* Form-native, not DataTable: these rows are entry-ordered draft
                  state with a remove control, not a list anybody sorts, filters
                  or exports. */}
              {lines.length === 0 ? (
                <EmptyState variant="line">
                  Nothing staged yet. Add a line above and it lands here, on one purchase order.
                </EmptyState>
              ) : (
                <LineOrder
                  enabled={lines.length > 1}
                  ids={lines.map((l) => l.key)}
                  labelFor={(key) => {
                    const l = lines.find((x) => x.key === key);
                    return l ? stagedLabel(l) : 'line';
                  }}
                  onMove={moveLine}
                  onCarry={setCarriedKey}
                >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-outline-variant">
                        <th className="py-1.5 w-8" aria-label="Move" />
                        <th className={TH}>#</th>
                        <th className={TH}>Item</th>
                        <th className={TH}>Rev</th>
                        <th className={TH_NUM}>Qty</th>
                        <th className={TH_NUM}>Unit</th>
                        <th className={TH_NUM}>Total</th>
                        <th className="py-1.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l, i) => {
                        const label = l.lineType === 'stock' ? itemLabel(l) : l.lineDescription || '';
                        return (
                          <tr
                            key={l.key}
                            className={`border-b border-outline-variant last:border-0 ${carriedKey === l.key ? CARRIED_ROW : ''}`}
                          >
                            <td className="py-1.5 pr-0">
                              {lines.length > 1 && (
                                <LineGrip id={l.key} label={stagedLabel(l)} onStep={(delta) => stepLine(l.key, delta)} />
                              )}
                            </td>
                            <td className={`${TD} tabular-nums text-on-surface-variant`}>{i + 1}</td>
                            {/* A PO line is a document the VENDOR reads too, so
                                this is the one place in the module that keeps
                                the SKU: the name leads, the code sits muted
                                under it. ItemName drops that second line by
                                itself when it would only repeat the first. */}
                            <td className={`${TD} text-on-surface`}>
                              {l.lineType === 'stock' ? (
                                <ItemName size="sm" showSku item={l} />
                              ) : (
                                <>
                                  {/* The description in its own element, not a
                                      bare text node beside the chip: a cell
                                      whose only text node is "Freight" and
                                      whose textContent is "FreightCharge" is
                                      unreachable by name. */}
                                  <span className="text-on-surface">{l.lineDescription}</span>
                                  <span
                                    className={`ml-1.5 inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${PO_LINE_TYPE_COLORS[l.lineType]}`}
                                  >
                                    {PO_LINE_TYPE_LABELS[l.lineType]}
                                  </span>
                                </>
                              )}
                              {l.notes && (
                                <span className="block max-w-[16rem] truncate text-xs text-on-surface-variant">
                                  {l.notes}
                                </span>
                              )}
                            </td>
                            <td className={`${TD} text-on-surface-variant`}>{l.revision || '—'}</td>
                            <td className={`${TD} text-right tabular-nums text-on-surface`}>{l.quantity}</td>
                            <td className={`${TD} text-right tabular-nums text-on-surface-variant`}>
                              {formatCents(l.unitCostCents)}
                            </td>
                            <td className={`${TD} text-right tabular-nums text-on-surface`}>
                              {l.unitCostCents === null ? '—' : formatCents(l.unitCostCents * l.quantity)}
                            </td>
                            <td className="py-1.5 text-right">
                              <Tooltip label={`Remove ${label}`}>
                                <IconButton size="sm" label={`Remove ${label}`} onClick={() => removeLine(l.key)}>
                                  <IconClose />
                                </IconButton>
                              </Tooltip>
                            </td>
                          </tr>
                        );
                      })}
                      {/* The discount, beneath the lines. Not a line: it has no
                          number, no grip, and it never moves. Its figure is
                          negative because that is what it does to the total. */}
                      {discount && (
                        <tr className="border-t border-outline-variant">
                          <td className="py-1.5 pr-0" />
                          <td className={TD} />
                          <td className={`${TD} text-on-surface`} colSpan={4}>
                            <span className="text-on-surface">Discount</span>
                            {discount.pct !== null && (
                              <span className="ml-1.5 tabular-nums text-on-surface-variant">{discount.pct}%</span>
                            )}
                            {discount.reason && (
                              <span className="block max-w-[16rem] truncate text-xs text-on-surface-variant">
                                {discount.reason}
                              </span>
                            )}
                          </td>
                          <td className={`${TD} text-right tabular-nums ${discountTooBig ? 'text-danger' : 'text-on-surface'}`}>
                            -{formatCents(discountCents)}
                          </td>
                          <td className="py-1.5 text-right">
                            <Tooltip label="Remove discount">
                              <IconButton size="sm" label="Remove discount" onClick={() => setDiscount(null)}>
                                <IconClose />
                              </IconButton>
                            </Tooltip>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                </LineOrder>
              )}
              <Reveal show={!!discount && lines.length === 0}>
                {discount && (
                  <p className="mt-2 text-sm text-on-surface-variant">
                    A discount of {discount.pct !== null ? `${discount.pct}%` : formatCents(discount.cents)} is staged.
                    It applies once there are lines.
                  </p>
                )}
              </Reveal>
            </Panel>

            {/* ---------------- Created this session ---------------- */}
            <Reveal show={created.length > 0}>
              <Alert tone="success" live={false} title="Created, waiting on review">
                {created.map((po) => (
                  <p key={po.id} className="text-sm text-on-surface">
                    <Link to={`/procurement/po/${po.id}`} className="font-mono text-primary hover:underline">
                      {po.po_number}
                    </Link>
                    <span className="text-on-surface-variant"> is a draft. Open it to submit for approval.</span>
                  </p>
                ))}
              </Alert>
            </Reveal>
          </div>

          {/* ---------------- The summary bar ----------------
              Sticky, and it renders at zero lines too, because it is where the
              page ends: the running total and the one act that finishes the
              document belong together and belong in view. Shadow is allowed
              here for the reason it is allowed on a modal — this floats over
              content it cannot predict. */}
          <div className="sticky bottom-0 z-10 mt-4 rounded-t-xl bg-surface-container px-4 py-3 shadow-lg sm:px-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-on-surface-variant">
                <span className="text-on-surface">
                  {lines.length} {lines.length === 1 ? 'line' : 'lines'}
                </span>
                {discount ? (
                  <>
                    {' · Subtotal '}
                    <span className="font-mono tabular-nums">{formatCents(subtotalCents)}</span>
                    {' · Discount '}
                    <span className={`font-mono tabular-nums ${discountTooBig ? 'text-danger' : ''}`}>
                      -{formatCents(discountCents)}
                    </span>
                    {' · Total '}
                    <span className="font-mono tabular-nums text-primary">{formatCents(totalCents)}</span>
                  </>
                ) : (
                  <>
                    {' · Subtotal '}
                    <span className="font-mono tabular-nums text-primary">{formatCents(subtotalCents)}</span>
                  </>
                )}
                <Appear show={someLineHasNoCost} className="ml-2 text-xs">(some lines have no cost)</Appear>
                <Appear show={discountTooBig} className="ml-2 text-xs text-danger">The discount is larger than the lines.</Appear>
                {/* Why the pill is off, said where the pill is. A disabled
                    button leaves the tab order, so this sentence is the only
                    way somebody not looking at it learns what it wants. */}
                <Appear show={!canCreate && !creating} className="ml-2 text-xs">
                  {!vendorId ? 'Pick a vendor to create the draft.' : 'Add a line to create the draft.'}
                </Appear>
              </p>
              <button type="button" onClick={handleCreate} disabled={!canCreate} className={buttonClass('filled')}>
                {creating ? 'Creating…' : 'Create draft PO'}
              </button>
            </div>
          </div>
        </div>
      </PageLayout>
  );
};

export default PurchaseOrderForm;
